import { useState } from "react";
import {
  ActionButton,
  AuthShell,
  ResultNotice,
  TextField,
} from "@elefante/auth-ui";

type Review = {
  user_code: string;
  status: string;
  client_id?: string;
  scope?: string;
  resource?: string | string[];
};

export function DevicePage({ initialCode }: { initialCode: string }) {
  const [code, setCode] = useState(initialCode);
  const [review, setReview] = useState<Review | null>(null);
  const [identity, setIdentity] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; message: string } | null>(
    null,
  );
  const verify = async () => {
    setBusy(true);
    setResult(null);
    setReview(null);
    try {
      const account = await fetch("/api/portal/account", {
        credentials: "same-origin",
      });
      if (account.status === 401)
        throw new Error(
          "Entre na conta Elefante vinculada a esta CLI e volte a esta página para verificar o código.",
        );
      const user = await account.json<{
        profile?: { email?: string; name?: string };
        user?: { email?: string };
      }>();
      if (!account.ok) throw new Error("Não foi possível verificar a conta.");
      setIdentity(
        user.profile?.email ??
          user.user?.email ??
          user.profile?.name ??
          "Conta Elefante autenticada",
      );
      const response = await fetch(
        `/api/auth/device?user_code=${encodeURIComponent(code.trim())}`,
        { credentials: "same-origin" },
      );
      const data = await response.json<Review>();
      if (!response.ok)
        throw new Error(
          "Código inválido ou expirado. Solicite um novo código à CLI.",
        );
      if (!data.client_id || !data.scope || !data.resource)
        throw new Error(
          "Esta solicitação pertence a outra identidade. Entre na conta vinculada a esta CLI; outra conta não pode autorizar em seu lugar.",
        );
      setReview(data);
    } catch (error) {
      setResult({
        ok: false,
        message:
          error instanceof Error
            ? error.message
            : "Não foi possível verificar o código.",
      });
    } finally {
      setBusy(false);
    }
  };
  const respond = async (accept: boolean) => {
    if (!review || busy) return;
    setBusy(true);
    setResult(null);
    try {
      const response = await fetch(
        `/api/auth/device/${accept ? "approve" : "deny"}`,
        {
          method: "POST",
          credentials: "same-origin",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ userCode: review.user_code }),
        },
      );
      if (!response.ok)
        throw new Error(
          "Não foi possível confirmar. O código pode ter expirado ou a identidade mudou; verifique novamente.",
        );
      setReview(null);
      setResult({
        ok: true,
        message: accept
          ? "Acesso autorizado para a sua identidade. Volte à tarefa no servidor. Você pode fechar esta página."
          : "Solicitação recusada. Nenhum acesso foi concedido.",
      });
    } catch (error) {
      setResult({
        ok: false,
        message:
          error instanceof Error
            ? error.message
            : "Não foi possível confirmar.",
      });
    } finally {
      setBusy(false);
    }
  };
  return (
    <AuthShell
      title="Autorizar a CLI do Controle Jurídico"
      intro="Confira o código recebido da sua tarefa e a conta que será usada. Autorize somente uma solicitação que você iniciou."
    >
      <a
        className="button button--secondary"
        href="/sign-in"
        target="_blank"
        rel="noreferrer"
      >
        Entrar na conta Elefante
      </a>
      <p>
        Depois de entrar, volte aqui. A autorização deve ser feita pela conta
        vinculada a esta CLI.
      </p>
      <TextField
        label="Código de confirmação"
        value={code}
        onChange={(event) => {
          setCode(event.target.value);
          setReview(null);
        }}
        autoComplete="off"
        maxLength={32}
      />
      <ActionButton
        busy={busy}
        disabled={!code.trim()}
        onClick={() => void verify()}
      >
        Verificar código
      </ActionButton>
      {review ? (
        <section aria-label="Permissões solicitadas">
          <p>
            <strong>Conta:</strong> {identity}
          </p>
          <p>
            <strong>Aplicativo:</strong> {review.client_id}
          </p>
          <p>
            <strong>Destino:</strong>{" "}
            {Array.isArray(review.resource)
              ? review.resource.join(", ")
              : review.resource}
          </p>
          <ul>
            {review.scope?.split(/\s+/u).map((scope) => (
              <li key={scope}>{scopeLabel(scope)}</li>
            ))}
          </ul>
          <p>
            Os escopos limitam o acesso; o papel atual no Controle continua
            obrigatório. Isso não autoriza transferências bancárias.
          </p>
          <ActionButton
            busy={busy}
            disabled={review.status !== "pending"}
            tone="primary"
            onClick={() => void respond(true)}
          >
            Autorizar acesso
          </ActionButton>
          <ActionButton
            busy={busy}
            disabled={review.status !== "pending"}
            onClick={() => void respond(false)}
          >
            Recusar
          </ActionButton>
        </section>
      ) : null}
      <ResultNotice result={result} />
    </AuthShell>
  );
}

function scopeLabel(scope: string): string {
  if (scope === "offline_access")
    return "Manter acesso até expirar ou ser revogado";
  const [, module, action] = scope.split(":");
  const modules: Record<string, string> = {
    people: "Pessoas",
    matters: "Contratos",
    cases: "Processos",
    finance: "Financeiro",
    documents: "Documentos",
    calendar: "Agenda",
    users: "Usuários",
    audit: "Auditoria",
    publications: "Publicações",
    overview: "Resumo",
    notifications: "Notificações",
    datajud: "DataJud",
  };
  const actions: Record<string, string> = {
    read: "consultar",
    create: "criar",
    update: "alterar",
    delete: "excluir",
    cancel: "cancelar",
    restore: "restaurar",
    purge: "excluir definitivamente",
    manage: "gerenciar",
    refresh: "atualizar dados externos",
  };
  return `${modules[module ?? ""] ?? module}: ${actions[action ?? ""] ?? action}`;
}
